import { useEffect, useMemo, useState } from 'react';
import { Lock, Unlock, FileDown, Calculator, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { cierresCajaApi, comprobantesCajaApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';
import { NOMBRE_CAJA } from '../components/ComprobanteTicket';

// 2026-10-22 (fase 3): cierre de caja DIARIO y POR CAJA. El servidor calcula todo: apertura
// (efectivo contado en el cierre anterior, o el del sistema en el primer cierre = arqueo
// inicial), ingresos y egresos con sus comprobantes RI/RE, efectivo esperado y diferencia con
// lo contado. Un día cerrado ya no se puede modificar; un dueño lo reabre con clave y motivo.
const CAJAS = ['ventas', 'servicios', 'mantenimiento', 'chica'];
const DENOMINACIONES = [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1];
const S = (x: any) => `S/ ${Number(x || 0).toFixed(2)}`;
const fechaTxt = (f: string) => String(f).slice(0, 10).split('-').reverse().join('/');

async function pdfCierre(cierre: any, movimientos: any[]) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  doc.setFontSize(16); doc.text('CIERRE DE CAJA', 105, 16, { align: 'center' });
  doc.setFontSize(11);
  doc.text(`${NOMBRE_CAJA[cierre.caja] || cierre.caja} — día ${fechaTxt(cierre.fecha)}`, 105, 23, { align: 'center' });
  doc.setFontSize(9);
  doc.text(`Cerrado por ${cierre.usuario} el ${new Date(cierre.creado_en).toLocaleString('es-PE')}${cierre.estado === 'reabierto' ? ' — REABIERTO' : ''}`, 105, 29, { align: 'center' });
  autoTable(doc, {
    startY: 34, theme: 'grid', styles: { fontSize: 10 }, headStyles: { fillColor: [30, 58, 138] },
    head: [['Concepto', 'Monto']],
    body: [
      [`Apertura en efectivo (${cierre.apertura_origen === 'arqueo_inicial' ? 'arqueo inicial: según sistema' : 'contado en el cierre anterior'})`, S(cierre.apertura_efectivo)],
      ['+ Ingresos en efectivo', S(cierre.ingresos_efectivo)],
      ['- Egresos en efectivo', S(cierre.egresos_efectivo)],
      ['= Efectivo que debe haber', S(cierre.esperado_efectivo)],
      ['Efectivo contado', S(cierre.contado_efectivo)],
      [Number(cierre.diferencia) === 0 ? 'Diferencia' : (Number(cierre.diferencia) > 0 ? 'Diferencia (SOBRA)' : 'Diferencia (FALTA)'), S(cierre.diferencia)],
      ['Ingresos por Yape (no están en la caja física)', S(cierre.ingresos_yape)],
      ['Egresos por Yape', S(cierre.egresos_yape)],
    ],
    columnStyles: { 1: { halign: 'right', cellWidth: 40 } },
  });
  let y = (doc as any).lastAutoTable.finalY + 6;
  if (cierre.conteo && Object.values(cierre.conteo).some((v: any) => Number(v) > 0)) {
    autoTable(doc, {
      startY: y, theme: 'striped', styles: { fontSize: 9 }, headStyles: { fillColor: [100, 116, 139] },
      head: [['Billete / moneda', 'Cantidad', 'Subtotal']],
      body: Object.entries(cierre.conteo).filter(([, v]) => Number(v) > 0)
        .map(([d, v]) => [`S/ ${Number(d).toFixed(2)}`, String(v), S(Number(d) * Number(v))]),
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }
  if (cierre.observaciones) { doc.setFontSize(10); doc.text(doc.splitTextToSize(`Observaciones: ${cierre.observaciones}`, 180), 15, y); y += 10; }
  autoTable(doc, {
    startY: y, theme: 'grid', styles: { fontSize: 8 }, headStyles: { fillColor: [30, 58, 138] },
    head: [['Comprobante', 'Fecha', 'Concepto', 'Forma', 'Ingreso', 'Egreso', 'Registró']],
    body: movimientos.map(m => [m.comprobante || (m.fondo ? 'Fondo' : '—'), new Date(m.fecha).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }),
      m.descripcion || m.concepto || m.nota || '', (m.metodo_pago || 'efectivo') === 'efectivo' ? 'Efectivo' : 'Yape',
      m.tipo === 'gasto' || m.tipo === 'RE' ? '' : S(m.monto), m.tipo === 'gasto' || m.tipo === 'RE' ? S(m.monto) : '', m.responsable || m.usuario || '']),
    columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' } },
  });
  y = Math.max((doc as any).lastAutoTable.finalY + 25, 250);
  if (y > 275) { doc.addPage(); y = 40; }
  doc.line(25, y, 85, y); doc.line(125, y, 185, y);
  doc.setFontSize(9); doc.text('Cajero', 55, y + 5, { align: 'center' }); doc.text('Dueño / revisó', 155, y + 5, { align: 'center' });
  doc.save(`Cierre_${cierre.caja}_${String(cierre.fecha).slice(0, 10)}.pdf`);
}

const CierreCaja = () => {
  const hoy = fechaLima();
  const [caja, setCaja] = useState('ventas');
  const [fecha, setFecha] = useState(hoy);
  const [r, setR] = useState<any>(null);
  const [historial, setHistorial] = useState<any[]>([]);
  const [conteo, setConteo] = useState<Record<string, string>>({});
  const [contadoManual, setContadoManual] = useState('');
  const [yapeOk, setYapeOk] = useState(false);
  const [obs, setObs] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [reabrir, setReabrir] = useState<any>(null);
  const [clave, setClave] = useState(''); const [motivo, setMotivo] = useState('');

  const cargar = async () => {
    try {
      const [res, hist] = await Promise.all([cierresCajaApi.resumen(caja, fecha), cierresCajaApi.historial(caja)]);
      setR(res); setHistorial(hist);
    } catch (e: any) { toast.error('Error: ' + e.message); }
  };
  useEffect(() => { setConteo({}); setContadoManual(''); setYapeOk(false); setObs(''); cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caja, fecha]);

  const totalConteo = useMemo(() => DENOMINACIONES.reduce((s, d) => s + d * (parseInt(conteo[d] || '0') || 0), 0), [conteo]);
  const contado = contadoManual !== '' ? parseFloat(contadoManual) || 0 : totalConteo;
  const diferencia = r ? Math.round((contado - r.esperado_efectivo) * 100) / 100 : 0;
  const hayYape = r && (r.ingresos_yape > 0 || r.egresos_yape > 0);
  const movsPdf = (rr: any) => [...(rr.movimientos || []), ...(rr.fondo || []).map((f: any) => ({ ...f, fondo: true, descripcion: `Fondo caja chica: ${f.tipo}${f.nota ? ' — ' + f.nota : ''}`, metodo_pago: 'efectivo', responsable: f.usuario }))];

  const cerrar = async () => {
    if (!r) return;
    if (contadoManual === '' && totalConteo === 0 && r.esperado_efectivo !== 0 &&
        !confirm('El efectivo contado es S/ 0.00. ¿Seguro que no hay dinero en la caja?')) return;
    if (!confirm(`¿Cerrar ${r.nombre_caja} del ${fechaTxt(fecha)}?\nEsperado ${S(r.esperado_efectivo)} · Contado ${S(contado)} · Diferencia ${S(diferencia)}\n\nDespués de cerrar, ese día ya no se puede modificar.`)) return;
    setGuardando(true);
    try {
      const cierre = await cierresCajaApi.cerrar({ caja, fecha, contado_efectivo: contado, yape_confirmado: yapeOk, observaciones: obs,
        conteo: Object.fromEntries(DENOMINACIONES.map(d => [String(d), parseInt(conteo[d] || '0') || 0])) });
      toast.success('Caja cerrada. Se descarga el PDF para firmar.');
      await pdfCierre(cierre, movsPdf(r));
      cargar();
    } catch (e: any) { toast.error(e.message); }
    finally { setGuardando(false); }
  };

  const pdfHistorial = async (c: any) => {
    const idx = historial.findIndex(h => h.id === c.id);
    const anterior = historial.slice(idx + 1).find(h => h.estado === 'cerrado');
    let desde = String(c.fecha).slice(0, 10);
    if (anterior) { const d = new Date(String(anterior.fecha).slice(0, 10) + 'T12:00:00'); d.setDate(d.getDate() + 1); desde = d.toISOString().slice(0, 10); }
    const comps = await comprobantesCajaApi.listar({ caja: c.caja, desde, hasta: String(c.fecha).slice(0, 10) });
    const nums = new Set<string>(c.comprobantes || []);
    await pdfCierre(c, comps.filter((x: any) => nums.has(x.numero)).reverse());
  };

  const confirmarReabrir = async () => {
    try {
      await cierresCajaApi.reabrir(reabrir.id, { clave, motivo });
      toast.success('Cierre reabierto: ya se puede corregir ese día. Luego vuelve a cerrarlo.');
      setReabrir(null); setClave(''); setMotivo(''); cargar();
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-wrap justify-between items-end gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3"><Lock className="text-blue-600" size={30} /> Cierre de caja</h1>
            <p className="text-gray-600 text-sm mt-1">Diario y por caja. Cuenta el efectivo, compáralo con lo que dice el sistema y cierra el día.</p>
          </div>
          <div><label className="block text-xs font-medium mb-1">Día</label>
            <input type="date" value={fecha} max={hoy} onChange={e => setFecha(e.target.value)} className="border rounded-lg px-3 py-2" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {CAJAS.map(k => (
            <button key={k} onClick={() => setCaja(k)}
              className={`px-4 py-2 rounded-lg font-semibold border ${caja === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50'}`}>
              {NOMBRE_CAJA[k]}
            </button>
          ))}
        </div>

        {r && r.cierre && (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5">
            <div className="flex flex-wrap justify-between gap-3 items-start">
              <div>
                <p className="text-lg font-bold text-green-800 flex items-center gap-2"><CheckCircle2 /> Caja cerrada — {fechaTxt(fecha)}</p>
                <p className="text-sm text-green-900">Cerró <b className="capitalize">{r.cierre.usuario}</b> el {new Date(r.cierre.creado_en).toLocaleString('es-PE')}</p>
                <p className="text-sm mt-2">Esperado {S(r.cierre.esperado_efectivo)} · Contado <b>{S(r.cierre.contado_efectivo)}</b> ·
                  Diferencia <b className={Number(r.cierre.diferencia) === 0 ? 'text-green-700' : 'text-red-700'}>{S(r.cierre.diferencia)}</b></p>
                {r.cierre.observaciones && <p className="text-sm mt-1">Observaciones: {r.cierre.observaciones}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => pdfHistorial(r.cierre)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border hover:bg-gray-50 text-sm"><FileDown size={16} /> PDF</button>
                <button onClick={() => setReabrir(r.cierre)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 border border-amber-300 hover:bg-amber-200 text-sm"><Unlock size={16} /> Reabrir</button>
              </div>
            </div>
          </div>
        )}

        {r && !r.cierre && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              {r.apertura_origen === 'arqueo_inicial' && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm text-amber-900 flex gap-2">
                  <AlertTriangle className="shrink-0" size={18} />
                  <span><b>Primer cierre de esta caja (arqueo inicial).</b> La apertura es el efectivo que calcula el sistema hasta ayer. Cuenta lo que realmente hay:
                    si no coincide, la diferencia queda documentada y desde mañana se parte de lo contado.</span>
                </div>
              )}
              {r.ultimo_cierre && r.fecha > r.ultimo_cierre.fecha && (() => {
                const d = new Date(r.ultimo_cierre.fecha + 'T12:00:00'); d.setDate(d.getDate() + 1);
                const desde = d.toISOString().slice(0, 10);
                return desde < r.fecha ? <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900">
                  Incluye los movimientos desde el {fechaTxt(desde)} (días sin cerrar desde el último cierre del {fechaTxt(r.ultimo_cierre.fecha)}).</div> : null;
              })()}
              <div className="bg-white rounded-xl shadow p-4">
                <h2 className="font-semibold mb-3">Movimientos ({r.movimientos.length + r.fondo.length})</h2>
                <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0"><tr>
                      <th className="px-2 py-1 text-left">Comprobante</th><th className="px-2 py-1 text-left">Hora</th><th className="px-2 py-1 text-left">Concepto</th>
                      <th className="px-2 py-1 text-left">Forma</th><th className="px-2 py-1 text-right">Ingreso</th><th className="px-2 py-1 text-right">Egreso</th><th className="px-2 py-1 text-left">Registró</th>
                    </tr></thead>
                    <tbody>
                      {[...r.fondo.map((f: any) => ({ ...f, fondo: true })), ...r.movimientos].map((m: any, i: number) => {
                        const sale = m.tipo === 'gasto';
                        return (
                          <tr key={i} className="border-t">
                            <td className={`px-2 py-1 font-mono ${sale ? 'text-red-700' : 'text-green-700'}`}>{m.fondo ? 'Fondo' : (m.comprobante || '—')}</td>
                            <td className="px-2 py-1">{new Date(m.fecha).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="px-2 py-1">{m.fondo ? `Fondo caja chica: ${m.tipo}${m.nota ? ' — ' + m.nota : ''}` : m.descripcion}</td>
                            <td className="px-2 py-1">{(m.metodo_pago || 'efectivo') === 'efectivo' ? 'Efectivo' : <span className="text-purple-700 font-semibold">Yape</span>}</td>
                            <td className="px-2 py-1 text-right text-green-700">{sale ? '' : S(m.monto)}</td>
                            <td className="px-2 py-1 text-right text-red-700">{sale ? S(m.monto) : ''}</td>
                            <td className="px-2 py-1 capitalize">{m.responsable || m.usuario || ''}</td>
                          </tr>);
                      })}
                      {r.movimientos.length + r.fondo.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-gray-500">Sin movimientos.</td></tr>}
                    </tbody>
                  </table>
                </div>
                {r.anulados.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">Comprobantes anulados en el periodo (no suman): {r.anulados.map((a: any) => a.numero).join(', ')}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow p-4 space-y-1 text-sm">
                <div className="flex justify-between"><span>Apertura (efectivo)</span><b>{S(r.apertura_efectivo)}</b></div>
                <div className="flex justify-between text-green-700"><span>+ Ingresos efectivo</span><b>{S(r.ingresos_efectivo)}</b></div>
                <div className="flex justify-between text-red-700"><span>− Egresos efectivo</span><b>{S(r.egresos_efectivo)}</b></div>
                <div className="flex justify-between border-t pt-2 text-base"><span>= Debe haber</span><b>{S(r.esperado_efectivo)}</b></div>
                <div className="flex justify-between text-purple-700 pt-2"><span>Yape: ingresos / egresos</span><b>{S(r.ingresos_yape)} / {S(r.egresos_yape)}</b></div>
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2"><Calculator size={18} /> Conteo del efectivo</h3>
                <div className="grid grid-cols-2 gap-2">
                  {DENOMINACIONES.map(d => (
                    <label key={d} className="flex items-center gap-2 text-sm">
                      <span className="w-16 text-right">S/ {d >= 1 ? d : d.toFixed(2)}</span>
                      <input type="number" min="0" step="1" value={conteo[d] || ''} onChange={e => { setConteo({ ...conteo, [d]: e.target.value }); setContadoManual(''); }}
                             className="w-16 border rounded px-2 py-1" placeholder="0" />
                    </label>
                  ))}
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium mb-1">Efectivo contado (S/)</label>
                  <input type="number" step="0.01" value={contadoManual !== '' ? contadoManual : totalConteo.toFixed(2)}
                         onChange={e => setContadoManual(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-lg font-bold" />
                </div>
                <div className={`mt-3 rounded-lg p-3 text-center font-bold ${diferencia === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {diferencia === 0 ? 'Cuadra exacto' : diferencia > 0 ? `Sobra ${S(diferencia)}` : `Falta ${S(-diferencia)}`}
                </div>
                {hayYape && (
                  <label className="flex items-start gap-2 mt-3 text-sm">
                    <input type="checkbox" checked={yapeOk} onChange={e => setYapeOk(e.target.checked)} className="mt-1 w-4 h-4" />
                    <span>Revisé en el celular que los Yape del día suman {S(r.ingresos_yape)} de ingreso{r.egresos_yape > 0 ? ` y ${S(r.egresos_yape)} de egreso` : ''}.</span>
                  </label>
                )}
                <label className="block text-xs font-medium mt-3 mb-1">Observaciones {diferencia !== 0 && <span className="text-red-600">(obligatorio: explica la diferencia)</span>}</label>
                <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
                <button onClick={cerrar} disabled={guardando}
                  className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
                  <Lock size={18} /> {guardando ? 'Cerrando…' : `Cerrar caja del ${fechaTxt(fecha)}`}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-4">
          <h2 className="font-semibold mb-3">Cierres anteriores — {NOMBRE_CAJA[caja]}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr>
                <th className="px-2 py-1 text-left">Día</th><th className="px-2 py-1 text-right">Debe haber</th><th className="px-2 py-1 text-right">Contado</th>
                <th className="px-2 py-1 text-right">Diferencia</th><th className="px-2 py-1 text-left">Cerró</th><th className="px-2 py-1 text-left">Estado</th><th></th>
              </tr></thead>
              <tbody>
                {historial.map(c => (
                  <tr key={c.id} className="border-t">
                    <td className="px-2 py-1">{fechaTxt(c.fecha)}</td>
                    <td className="px-2 py-1 text-right">{S(c.esperado_efectivo)}</td>
                    <td className="px-2 py-1 text-right">{S(c.contado_efectivo)}</td>
                    <td className={`px-2 py-1 text-right font-semibold ${Number(c.diferencia) === 0 ? 'text-green-700' : 'text-red-700'}`}>{S(c.diferencia)}</td>
                    <td className="px-2 py-1 capitalize">{c.usuario}</td>
                    <td className="px-2 py-1">{c.estado === 'cerrado' ? 'Cerrado' : <span className="text-amber-700" title={c.reabierto_motivo}>Reabierto por {c.reabierto_por}</span>}</td>
                    <td className="px-2 py-1 text-right"><button onClick={() => pdfHistorial(c)} className="text-blue-600 hover:underline text-xs">PDF</button></td>
                  </tr>
                ))}
                {historial.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-gray-500">Todavía no hay cierres de esta caja.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {reabrir && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-lg w-full max-w-sm p-5 space-y-3">
            <h3 className="font-semibold flex items-center gap-2"><Unlock className="text-amber-600" /> Reabrir cierre del {fechaTxt(reabrir.fecha)}</h3>
            <p className="text-sm text-gray-600">Permite corregir movimientos de ese día. Queda en Auditoría. Después hay que volver a cerrarlo.</p>
            <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input type="password" value={clave} onChange={e => setClave(e.target.value)} placeholder="Clave de administrador" autoComplete="off" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <button onClick={confirmarReabrir} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 rounded-lg">Reabrir</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CierreCaja;
